/**
 * Core backtest engine implementation
 */
import { 
  HistoricalPrice, 
  BacktestPortfolio, 
  BacktestOptions, 
  BacktestResult,
  BacktestSignal,
  BacktestTrade,
  BacktestPosition,
  TradeExitReason,
  SymbolSettings,
  TradeDirection
} from './types.js';
import { calculatePerformanceMetrics } from './metrics.js';
import { generateTradingDays, findNextTradingDay } from './utils.js';

/**
 * Main backtest function that processes historical data based on strategy parameters
 */
export async function runBacktest(
  historicalData: HistoricalPrice[], 
  options: BacktestOptions,
  signalGenerator: (data: HistoricalPrice[], date: string, options: BacktestOptions) => BacktestSignal[]
): Promise<BacktestResult> {
  // Validate inputs
  if (!historicalData.length) {
    throw new Error('Historical data is empty');
  }
  
  // Check data quality and completeness
  const dataValidationResult = validateDataCompleteness(historicalData, options);
  if (!dataValidationResult.isValid) {
    throw new Error(`Insufficient historical data: ${dataValidationResult.reason}`);
  }

  // Initialize portfolio
  const portfolio: BacktestPortfolio = {
    cash: options.initialCapital,
    positions: {},
    equity: [{
      date: options.startDate,
      value: options.initialCapital
    }],
    trades: [],
    initialCapital: options.initialCapital
  };

  // Get all unique, sorted trading days from historical data
  const tradingDays = generateTradingDays(historicalData);

  // Process each trading day
  for (const day of tradingDays) {
    // Skip days before start date or after end date
    if (day < options.startDate || day > options.endDate) {
      continue;
    }

    // Get data for current day
    const dayData = historicalData.filter(d => d.date.startsWith(day));
    if (!dayData.length) continue;

    // Update current prices for all positions
    updatePositionPrices(portfolio, dayData);

    // Process existing positions (check for stop loss/take profit)
    handleExistingPositions(portfolio, dayData, options);

    // Generate new signals for this day
    const signals = signalGenerator(historicalData, day, options);

    // Execute trades based on signals
    if (signals.length > 0) {
      executeTrades(portfolio, signals, dayData, options);
    }

    // Record daily equity
    recordDailyEquity(portfolio, day, dayData);
  }

  // Close any remaining open positions at the end of the backtest
  closeAllPositions(portfolio, historicalData, TradeExitReason.END_OF_BACKTEST);

  // Calculate performance metrics
  const result = calculatePerformanceMetrics(portfolio, options);
  
  return result;
}

/**
 * Validate data completeness and continuity to ensure accurate backtesting
 */
function validateDataCompleteness(
  historicalData: HistoricalPrice[], 
  options: BacktestOptions
): { isValid: boolean; reason?: string } {
  if (historicalData.length === 0) {
    return { isValid: false, reason: 'Historical data is empty' };
  }
  
  // Get all unique symbols
  const symbols = [...new Set(historicalData.map(d => d.symbol))];
  
  // Verify all requested symbols have data
  const requestedSymbols = options.symbolSettings.map(s => s.symbol);
  const missingSymbols = requestedSymbols.filter(s => !symbols.includes(s));
  if (missingSymbols.length > 0) {
    return { 
      isValid: false, 
      reason: `Missing data for the following symbols: ${missingSymbols.join(', ')}` 
    };
  }
  
  // Get the latest data point to determine the application timeline
  const latestDataPoints = historicalData.sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  ).slice(0, 100); // Look at most recent 100 points
  
  // Find the most recent date in our data
  const mostRecentDate = latestDataPoints[0]?.date.split('T')[0] || '';
  
  // Check if we have any future/synthetic data points
  const hasFutureData = historicalData.some(point => point.isFuture === true);
  
  // Calculate the last valid date for backtesting
  // FIX: Use actual current date instead of data date for validation
  const currentDate = new Date();
  const currentDateString = currentDate.toISOString().split('T')[0];
  
  // For historical data validation, use the most recent between current date and data date
  const lastValidDate = mostRecentDate > currentDateString ? currentDateString : mostRecentDate;
  
  // Dynamically determine if end date exceeds available data
  const requestedEndDate = new Date(options.endDate);
  const validEndDate = new Date(lastValidDate);
  
  // FIX: More permissive validation - only reject if end date is more than 1 day in the future
  const futureThreshold = new Date(currentDate);
  futureThreshold.setDate(futureThreshold.getDate() + 1); // Allow up to tomorrow
  
  // If end date exceeds available data and we don't have synthetic future data, reject
  if (requestedEndDate > futureThreshold && !hasFutureData) {
    return {
      isValid: false,
      reason: `End date (${options.endDate}) is too far in the future. Historical data is only available up to ${currentDateString}.`
    };
  }
  
  // Generate full date range
  const tradingDays = generateTradingDays(historicalData);
  
  // Check date range coverage for the requested period
  // First check if we have data that includes the start and end dates
  const startDateFound = tradingDays.some(day => day <= options.startDate);
  const endDateFound = tradingDays.some(day => day >= options.endDate) || 
                       (hasFutureData && options.endDate <= mostRecentDate) ||
                       (requestedEndDate <= futureThreshold); // Allow recent dates
  
  if (!startDateFound || !endDateFound) {
    if (hasFutureData && !endDateFound) {
      // We have synthetic future data, but it might not extend far enough
      return { 
        isValid: false, 
        reason: `Future data doesn't extend to the requested end date (${options.endDate}). Latest available date: ${mostRecentDate}` 
      };
    }
    
    return { 
      isValid: false, 
      reason: `Data doesn't cover the requested date range (${options.startDate} to ${options.endDate})` 
    };
  }
  
  // Check data density (should have data for at least 90% of trading days)
  const daysInRange = tradingDays.filter(day => day >= options.startDate && day <= options.endDate);
  
  // Generate expected trading days (excluding weekends)
  const expectedDays = generateExpectedTradingDays(options.startDate, options.endDate);
  
  const coverage = daysInRange.length / expectedDays.length;
  if (coverage < 0.9) {
    // If we have future data, we can be more lenient about coverage
    if (hasFutureData && coverage >= 0.7) {
      console.warn(`Using partial data coverage (${(coverage * 100).toFixed(1)}%) with synthetic future data`);
    } else {
      return { 
        isValid: false, 
        reason: `Insufficient data coverage: only ${(coverage * 100).toFixed(1)}% of trading days have data` 
      };
    }
  }
  
  // Check if each symbol has sufficient data
  for (const symbol of symbols) {
    const symbolData = historicalData.filter(d => d.symbol === symbol);
    const symbolDays = [...new Set(symbolData.map(d => d.date.split('T')[0]))];
    
    // Each symbol should have data for at least 90% of trading days
    const symbolCoverage = symbolDays.length / daysInRange.length;
    
    // Be more lenient about coverage if we have future data
    const minCoverage = hasFutureData ? 0.7 : 0.9;
    
    if (symbolCoverage < minCoverage) {
      return { 
        isValid: false, 
        reason: `Symbol ${symbol} has insufficient data coverage (${(symbolCoverage * 100).toFixed(1)}%)` 
      };
    }
    
    // Check for large gaps (more than 5 consecutive trading days without data)
    // Be more lenient if we have future data
    const maxAllowedGap = hasFutureData ? 10 : 5;
    const maxGap = findLargestDataGap(symbolDays, daysInRange);
    
    if (maxGap > maxAllowedGap) {
      return { 
        isValid: false, 
        reason: `Symbol ${symbol} has data gaps (largest gap: ${maxGap} trading days)` 
      };
    }
  }
  
  return { isValid: true };
}

/**
 * Generate expected trading days (weekdays) between start and end dates
 */
function generateExpectedTradingDays(startDate: string, endDate: string): string[] {
  const days: string[] = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  let current = new Date(start);
  while (current <= end) {
    // Skip weekends (0 = Sunday, 6 = Saturday)
    const dayOfWeek = current.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      days.push(current.toISOString().split('T')[0]);
    }
    
    // Move to next day
    current.setDate(current.getDate() + 1);
  }
  
  return days;
}

/**
 * Find the largest gap (in trading days) in a symbol's data
 */
function findLargestDataGap(symbolDays: string[], allDays: string[]): number {
  // Sort both arrays
  const sortedSymbolDays = [...symbolDays].sort();
  const sortedAllDays = [...allDays].sort();
  
  // Find days in allDays that are missing from symbolDays
  const missingDays = sortedAllDays.filter(day => !sortedSymbolDays.includes(day));
  
  if (missingDays.length === 0) return 0;
  
  // Find consecutive sequences of missing days
  let maxGap = 0;
  let currentGap = 1;
  
  for (let i = 1; i < missingDays.length; i++) {
    const prevDay = new Date(missingDays[i-1]);
    const currDay = new Date(missingDays[i]);
    
    // Check if days are consecutive in the original allDays array
    const prevIndex = sortedAllDays.indexOf(missingDays[i-1]);
    const currIndex = sortedAllDays.indexOf(missingDays[i]);
    
    if (currIndex - prevIndex === 1) {
      // Days are consecutive in trading days
      currentGap++;
    } else {
      // Non-consecutive gap
      maxGap = Math.max(maxGap, currentGap);
      currentGap = 1;
    }
  }
  
  // Check the last gap
  maxGap = Math.max(maxGap, currentGap);
  
  return maxGap;
}

/**
 * Updates current prices for all positions in the portfolio
 */
function updatePositionPrices(portfolio: BacktestPortfolio, dayData: HistoricalPrice[]): void {
  for (const symbol in portfolio.positions) {
    const symbolData = dayData.find(d => d.symbol === symbol);
    if (symbolData) {
      portfolio.positions[symbol].currentPrice = symbolData.close;
    }
  }
}

/**
 * Handles existing positions, checking for stop-loss and take-profit conditions
 */
function handleExistingPositions(
  portfolio: BacktestPortfolio, 
  dayData: HistoricalPrice[],
  options: BacktestOptions
): void {
  for (const symbol in portfolio.positions) {
    const position = portfolio.positions[symbol];
    const symbolData = dayData.find(d => d.symbol === symbol);
    
    // Skip if no price data for this symbol today
    if (!symbolData) continue;
    
    const currentPrice = symbolData.close;
    
    // Get symbol-specific parameters if available
    const symbolSettings = getSymbolSettings(options, symbol);
    const profitTargetPct = getSymbolProfitTarget(symbolSettings, options);
    const stopLossPct = getSymbolStopLoss(symbolSettings, options);
    
    console.log(`Processing position for ${symbol} at price ${currentPrice}:`);
    console.log(`- Direction: ${position.direction}`);
    console.log(`- Entry price: ${position.entryPrice}`);
    console.log(`- Applied profit target: ${profitTargetPct * 100}%`);
    console.log(`- Applied stop loss: ${stopLossPct * 100}%`);
    
    // Calculate take profit and stop loss prices if not already set
    if (!position.takeProfitPrice) {
      position.takeProfitPrice = position.direction === 'long'
        ? position.entryPrice * (1 + profitTargetPct)
        : position.entryPrice * (1 - profitTargetPct);
      console.log(`- Setting take profit price: ${position.takeProfitPrice}`);
    }
    
    if (!position.stopLossPrice) {
      position.stopLossPrice = position.direction === 'long'
        ? position.entryPrice * (1 - stopLossPct)
        : position.entryPrice * (1 + stopLossPct);
      console.log(`- Setting stop loss price: ${position.stopLossPrice}`);
    }
    
    // Check for take profit condition
    if (position.direction === 'long' && symbolData.high >= position.takeProfitPrice) {
      console.log(`*** TAKE PROFIT triggered for ${symbol} at ${position.takeProfitPrice} ***`);
      // Use take profit price for exit (conservative approach)
      closePosition(portfolio, symbol, position.takeProfitPrice, symbolData.date, TradeExitReason.TAKE_PROFIT);
      continue;
    } else if (position.direction === 'short' && symbolData.low <= position.takeProfitPrice) {
      console.log(`*** TAKE PROFIT triggered for ${symbol} at ${position.takeProfitPrice} ***`);
      closePosition(portfolio, symbol, position.takeProfitPrice, symbolData.date, TradeExitReason.TAKE_PROFIT);
      continue;
    }
    
    // Check for stop loss condition
    if (position.direction === 'long' && symbolData.low <= position.stopLossPrice) {
      console.log(`*** STOP LOSS triggered for ${symbol} at ${position.stopLossPrice} ***`);
      // Use stop loss price for exit (conservative approach)
      closePosition(portfolio, symbol, position.stopLossPrice, symbolData.date, TradeExitReason.STOP_LOSS);
      continue;
    } else if (position.direction === 'short' && symbolData.high >= position.stopLossPrice) {
      console.log(`*** STOP LOSS triggered for ${symbol} at ${position.stopLossPrice} ***`);
      closePosition(portfolio, symbol, position.stopLossPrice, symbolData.date, TradeExitReason.STOP_LOSS);
      continue;
    }
  }
}

/**
 * Execute trades based on generated signals
 */
function executeTrades(
  portfolio: BacktestPortfolio,
  signals: BacktestSignal[],
  dayData: HistoricalPrice[],
  options: BacktestOptions
): void {
  // First process all exit signals
  const exitSignals = signals.filter(s => s.direction === 'exit');
  for (const signal of exitSignals) {
    if (portfolio.positions[signal.symbol]) {
      const symbolData = dayData.find(d => d.symbol === signal.symbol);
      if (symbolData) {
        closePosition(
          portfolio, 
          signal.symbol, 
          signal.price || symbolData.close, 
          symbolData.date, 
          TradeExitReason.SIGNAL_EXIT
        );
      }
    }
  }
  
  // Process entry signals - sorted by signal strength
  const entrySignals = signals
    .filter(s => s.direction === 'long' || s.direction === 'short')
    .sort((a, b) => b.strength - a.strength);
  
  for (const signal of entrySignals) {
    // Skip if we already have a position in this symbol
    if (portfolio.positions[signal.symbol]) {
      continue;
    }
    
    const symbolData = dayData.find(d => d.symbol === signal.symbol);
    if (!symbolData) continue;
    
    const price = signal.price || symbolData.close;
    const availableCash = portfolio.cash;
    
    // Skip if not enough cash
    if (availableCash <= 0) continue;
    
    // Check if adding this position would exceed max strategy exposure
    const currentEquity = calculateCurrentEquity(portfolio, dayData);
    const currentExposure = (currentEquity - availableCash) / currentEquity;
    if (currentExposure >= options.maxStrategyExposure) continue;
    
    // Get symbol-specific position size if available
    const symbolSettings = getSymbolSettings(options, signal.symbol);
    const positionSizePct = getSymbolPositionSize(symbolSettings, options);
    
    // Calculate position size in cash terms
    const positionCash = currentEquity * positionSizePct;
    const actualCash = Math.min(positionCash, availableCash);
    
    // Calculate quantity and account for slippage
    const slippagePrice = signal.direction === 'long' 
      ? price * (1 + options.slippageRate) 
      : price * (1 - options.slippageRate);
    
    const quantity = Math.floor(actualCash / slippagePrice);
    if (quantity <= 0) continue;
    
    // Calculate commission
    const commission = price * quantity * options.commissionRate;
    
    // Ensure we have enough cash after accounting for commission
    if (quantity * slippagePrice + commission > availableCash) continue;
    
    // Open the position
    const actualEntryPrice = slippagePrice;
    portfolio.positions[signal.symbol] = {
      symbol: signal.symbol,
      quantity,
      entryPrice: actualEntryPrice,
      entryDate: symbolData.date,
      direction: signal.direction === 'long' ? 'long' : 'short',
      currentPrice: actualEntryPrice
    };
    
    // Deduct cash
    portfolio.cash -= (quantity * actualEntryPrice + commission);
  }
}

/**
 * Close a position and record the trade
 */
function closePosition(
  portfolio: BacktestPortfolio,
  symbol: string,
  exitPrice: number,
  exitDate: string,
  reason: TradeExitReason
): void {
  const position = portfolio.positions[symbol];
  if (!position) return;
  
  // Calculate profit/loss
  const profit = position.direction === 'long'
    ? (exitPrice - position.entryPrice) * position.quantity
    : (position.entryPrice - exitPrice) * position.quantity;
  
  const profitPercent = position.direction === 'long'
    ? ((exitPrice - position.entryPrice) / position.entryPrice) * 100
    : ((position.entryPrice - exitPrice) / position.entryPrice) * 100;
  
  // Record the trade
  const trade: BacktestTrade = {
    symbol,
    entryDate: position.entryDate,
    exitDate,
    entryPrice: position.entryPrice,
    exitPrice,
    quantity: position.quantity,
    profit,
    profitPercent,
    direction: position.direction,
    reason
  };
  
  portfolio.trades.push(trade);
  
  // Add cash back to portfolio
  portfolio.cash += position.quantity * exitPrice;
  
  // Remove position
  delete portfolio.positions[symbol];
}

/**
 * Close all remaining open positions at the end of backtest
 */
function closeAllPositions(
  portfolio: BacktestPortfolio,
  historicalData: HistoricalPrice[],
  reason: TradeExitReason
): void {
  // Group historical data by symbol for easy lookup
  const latestPrices = new Map<string, HistoricalPrice>();
  
  for (const dataPoint of historicalData) {
    const existing = latestPrices.get(dataPoint.symbol);
    if (!existing || new Date(dataPoint.date) > new Date(existing.date)) {
      latestPrices.set(dataPoint.symbol, dataPoint);
    }
  }
  
  // Close each position with the latest price
  for (const symbol in portfolio.positions) {
    const latestData = latestPrices.get(symbol);
    if (latestData) {
      closePosition(
        portfolio,
        symbol,
        latestData.close,
        latestData.date,
        reason
      );
    }
  }
}

/**
 * Record daily equity in the portfolio
 */
function recordDailyEquity(
  portfolio: BacktestPortfolio,
  date: string,
  dayData: HistoricalPrice[]
): void {
  const equity = calculateCurrentEquity(portfolio, dayData);
  
  portfolio.equity.push({
    date,
    value: equity
  });
}

/**
 * Calculate current equity value
 */
function calculateCurrentEquity(
  portfolio: BacktestPortfolio,
  currentPrices: HistoricalPrice[]
): number {
  let equity = portfolio.cash;
  
  // Add value of all positions
  for (const symbol in portfolio.positions) {
    const position = portfolio.positions[symbol];
    const symbolData = currentPrices.find(d => d.symbol === symbol);
    
    if (symbolData) {
      equity += position.quantity * symbolData.close;
    } else if (position.currentPrice) {
      equity += position.quantity * position.currentPrice;
    }
  }
  
  return equity;
}

/**
 * Get symbol-specific settings or undefined if not found
 */
function getSymbolSettings(options: BacktestOptions, symbol: string): SymbolSettings | undefined {
  return options.symbolSettings.find(s => s.symbol === symbol);
}

/**
 * Get symbol-specific profit target or default
 */
function getSymbolProfitTarget(symbolSettings: SymbolSettings | undefined, options: BacktestOptions): number {
  console.log(`Getting profit target for symbol settings:`, symbolSettings);
  if (symbolSettings?.profitTarget) {
    const targetValue = parseFloat(symbolSettings.profitTarget) / 100;
    console.log(`Using symbol-specific profit target: ${targetValue * 100}%`);
    return targetValue;
  }
  console.log(`Using default profit target: ${options.defaultProfitTarget * 100}%`);
  return options.defaultProfitTarget;
}

/**
 * Get symbol-specific stop loss or default
 */
function getSymbolStopLoss(symbolSettings: SymbolSettings | undefined, options: BacktestOptions): number {
  console.log(`Getting stop loss for symbol settings:`, symbolSettings);
  if (symbolSettings?.stopLoss) {
    const stopValue = parseFloat(symbolSettings.stopLoss) / 100;
    console.log(`Using symbol-specific stop loss: ${stopValue * 100}%`);
    return stopValue;
  }
  console.log(`Using default stop loss: ${options.defaultStopLoss * 100}%`);
  return options.defaultStopLoss;
}

/**
 * Get symbol-specific position size or default
 */
function getSymbolPositionSize(symbolSettings: SymbolSettings | undefined, options: BacktestOptions): number {
  if (symbolSettings?.maxPositionSize) {
    return parseFloat(symbolSettings.maxPositionSize) / 100;
  }
  return options.maxPositionSize;
}